import { useEffect, useRef, useState } from "react";
import { MapPin, Star, Columns3, Share2, Volume2, VolumeX, Copy, ExternalLink, Phone, Clock, Car, Tag, BedDouble, Bath, Maximize, LandPlot, Hammer, Layers, Ruler, CalendarDays, Mail, Globe, X, UserRound, Clapperboard, Palette, PanelsTopLeft, MessageCircle, BarChart3, Sparkles } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { asset, money } from "@/lib/api";
import { fmtDims } from "@/lib/units";

export const FACT_KEYS = [["status", "Status"], ["price", "Price"], ["beds", "Beds"], ["baths", "Baths"], ["sqft", "Size"], ["garage", "Garage"], ["lot", "Lot"], ["year", "Year built"], ["storeys", "Storeys"], ["room_size", "Room size"]];
const ICONS = { price: Tag, beds: BedDouble, baths: Bath, sqft: Maximize, garage: Car, lot: LandPlot, year: Hammer, storeys: Layers, room_size: Ruler };
const factValue = (k, x, h, unit) => ({
  status: [x.listing_status, ""],
  price: [x.base_price ? money(x.base_price) : "", "Price"],
  beds: [x.beds || "", x.beds === 1 ? "Bedroom" : "Bedrooms"],
  baths: [x.baths || "", x.baths === 1 ? "Bathroom" : "Bathrooms"],
  sqft: [x.sqft ? (unit === "m" ? Math.round(x.sqm || x.sqft * 0.092903).toLocaleString() : x.sqft.toLocaleString()) : "", unit === "m" ? "m²" : "Sq ft"],
  garage: [x.garage, "Garage"],
  lot: [x.lot_size, "Lot size"],
  year: [x.year_built || "", "Year built"],
  storeys: [x.storeys || "", x.storeys === 1 ? "Storey" : "Storeys"],
  room_size: [h?.dimensions ? fmtDims(h.dimensions, unit) : "", h?.title || "Room size"],
}[k] || ["", ""]);
export const homeFacts = (keys, house, h, unit) => (keys || []).map((k) => { const [value, label] = factValue(k, house, h, unit); return { k, Icon: ICONS[k], value: String(value || ""), label }; }).filter((f) => f.value);
export const CLOSING_FACTS = ["status", "beds", "baths", "sqft", "garage", "lot", "year", "storeys"];
import { vt } from "@/lib/industry";
import { jumpTo } from "./useMatterport";

export function useAmbient(url, active) {
  const ref = useRef(null);
  const [muted, setMuted] = useState(() => localStorage.getItem("tour_muted") === "1");
  useEffect(() => {
    if (!url) return undefined;
    const a = new Audio(asset(url));
    a.loop = true;
    a.volume = 0.35;
    ref.current = a;
    return () => { a.pause(); ref.current = null; };
  }, [url]);
  useEffect(() => {
    const a = ref.current;
    if (!a) return undefined;
    if (!active || muted) { a.pause(); return undefined; }
    const go = () => a.play().catch(() => {});
    go();
    window.addEventListener("pointerdown", go, { once: true });
    return () => window.removeEventListener("pointerdown", go);
  }, [active, muted, url]);
  const toggle = () => setMuted((m) => { localStorage.setItem("tour_muted", m ? "0" : "1"); return !m; });
  return [muted, toggle];
}

export function useGlide(sdk, sweepsRef, steps, run, returnRef, returnMode = "fly", closingMs = 0, poseRef = null) {
  const [cap, setCap] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [closing, setClosing] = useState(false);
  const done = useRef(false);
  useEffect(() => {
    if (!run || !sdk || done.current || steps.length === 0) return undefined;
    done.current = true;
    let stop = false;
    const home = poseRef?.current?.sweep ? { sweep: poseRef.current.sweep, rotation: poseRef.current.rotation } : null;
    setPlaying(true);
    const cancel = () => { stop = true; setCap(null); setPlaying(false); setClosing(false); };
    const t = setTimeout(() => window.addEventListener("pointerdown", cancel, { once: true }), 300);
    (async () => {
      for (let i = 0; i < steps.length && !stop; i += 1) {
        const { h, caption, transition, facts, auto, hold, tip } = steps[i];
        setCap({ text: caption || h?.title || "", facts: facts || [], tip, n: i, of: steps.length });
        if (h) await jumpTo(sdk, { ...(h.pose || {}), position: h.pose?.position || h.position }, transition || "fly", "slow", sweepsRef.current);
        else if (auto) {
          const all = Object.values(sweepsRef.current || {}).filter((s) => s.enabled !== false);
          const hs = home && all.find((s) => (s.sid || s.id) === home.sweep);
          const floor = hs?.floorInfo?.sequence;
          const same = floor == null ? [] : all.filter((s) => s.floorInfo?.sequence === floor);
          const list = same.length >= 4 ? same : all;
          const s = list[Math.floor(((i + 1) * list.length) / (steps.length + 1))];
          if (s) await jumpTo(sdk, { sweep: s.sid || s.id }, transition || "fly", "slow", sweepsRef.current);
        }
        await new Promise((r) => { setTimeout(r, hold || 3200); });
      }
      setCap(null);
      if (!stop && closingMs) {
        setClosing(true);
        await new Promise((r) => { setTimeout(r, closingMs); });
        setClosing(false);
      }
      window.removeEventListener("pointerdown", cancel);
      if (!stop && (returnRef?.current || home)) await jumpTo(sdk, returnRef?.current || home, returnMode, "slow", sweepsRef.current);
      setPlaying(false);
    })();
    return () => { clearTimeout(t); window.removeEventListener("pointerdown", cancel); };
  }, [run, sdk, steps, sweepsRef, returnRef, returnMode, closingMs, poseRef]);
  return [cap, playing, closing];
}

export function IntroVideo({ src, withSound, onDone }) {
  const [muted, setMuted] = useState(!withSound);
  return (
    <div className="fixed inset-0 z-[60] bg-black grid place-items-center" data-testid="intro-video">
      <video src={asset(src)} autoPlay playsInline muted={muted} onEnded={onDone} onError={onDone} className="w-full h-full object-contain" data-testid="intro-video-player" />
      <div className="absolute bottom-6 right-6 flex gap-2">
        {muted && <button onClick={() => setMuted(false)} className="h-10 px-4 rounded-full glass-dark text-sm font-semibold inline-flex items-center gap-2" data-testid="intro-video-unmute"><Volume2 className="w-4 h-4" />Sound on</button>}
        <button onClick={onDone} className="h-10 px-4 rounded-full glass-dark text-sm font-semibold" data-testid="intro-video-skip">Skip</button>
      </div>
    </div>
  );
}

function FactChips({ facts, big }) {
  const status = facts.find((f) => f.k === "status");
  const rest = facts.filter((f) => f.k !== "status");
  return (
    <>
      {status && <span className="inline-block mb-3 rounded-full bg-[#D4A12A] text-slate-900 text-xs font-bold uppercase tracking-[0.18em] px-4 py-1.5" data-testid="glide-status">{status.value}</span>}
      {rest.length > 0 && <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mt-6" data-testid="glide-facts">
        {rest.map(({ k, Icon, value, label }, i) => (
          <div key={k} style={{ animationDelay: `${250 + i * 160}ms` }} className={`rise cine-tile relative overflow-hidden rounded-3xl backdrop-blur-xl flex flex-col items-center text-center ${big ? "w-[124px] px-3 py-4" : "w-[104px] sm:w-[150px] px-2 sm:px-3 py-3 sm:py-5"}`} data-testid={`glide-fact-${k}`}>
            <span className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-[#D4A12A] to-transparent" />
            <span className={`grid place-items-center rounded-2xl bg-gradient-to-br from-[#F3D27A] to-[#B8861B] text-slate-950 shadow-[0_8px_24px_-6px_rgba(212,161,42,0.7)] ${big ? "w-11 h-11" : "w-12 h-12 sm:w-14 sm:h-14"}`}>{Icon && <Icon className={big ? "w-5 h-5" : "w-6 h-6 sm:w-7 sm:h-7"} strokeWidth={1.6} />}</span>
            <span className={`cine-v font-serif leading-tight mt-3 ${value.length > 10 ? "text-base sm:text-lg" : big ? "text-xl" : "text-2xl sm:text-3xl"}`}>{value}</span>
            <span className="text-[10px] sm:text-[11px] cine-l uppercase tracking-[0.2em] mt-1 line-clamp-1">{label}</span>
          </div>
        ))}
      </div>}
    </>
  );
}

const TIP_ICONS = { clapper: Clapperboard, palette: Palette, menu: PanelsTopLeft, pin: MapPin, ruler: Ruler, chat: MessageCircle, chart: BarChart3, calendar: CalendarDays };

export function TryTip({ cap }) {
  if (!cap?.tip) return null;
  const [icon, title, text] = cap.tip;
  const Icon = TIP_ICONS[icon] || Sparkles;
  return (
    <div key={cap.n} className="absolute z-[25] top-6 left-4 right-4 sm:left-auto sm:right-6 sm:top-8 sm:w-[22rem] pointer-events-none" data-testid="try-tip">
      <div className="rise cine-card rounded-3xl p-5 shadow-2xl" style={{ animationDelay: "500ms" }}>
        <div className="flex items-center justify-between">
          <span className="w-11 h-11 rounded-2xl grid place-items-center bg-gradient-to-br from-[#F3D27A] to-[#B8861B] text-slate-950"><Icon className="w-5 h-5" strokeWidth={1.7} /></span>
          <span className="text-[10px] uppercase tracking-[0.25em] opacity-60" data-testid="try-tip-count">{cap.n + 1} / {cap.of}</span>
        </div>
        <p className="font-serif text-xl mt-3 leading-snug" data-testid="try-tip-title">{title}</p>
        <p className="text-sm opacity-75 mt-1.5 leading-relaxed">{text}</p>
      </div>
    </div>
  );
}

export function TryTitle({ show, name }) {
  return (
    <div className={`absolute inset-0 z-[55] grid place-items-center bg-[#050d18] text-white text-center px-6 transition-opacity duration-1000 ${show ? "opacity-100" : "opacity-0 pointer-events-none"}`} data-testid="try-title-card">
      {show && (
        <div>
          <p className="rise text-xs uppercase tracking-[0.4em] text-[#E9C25A]">Prepared for {name}</p>
          <h1 className="rise font-serif text-5xl sm:text-7xl lg:text-8xl mt-5" style={{ animationDelay: "350ms" }}>Your tour,<br /><em className="text-[#E9C25A]">reimagined.</em></h1>
          <div className="rise mx-auto mt-8 h-px w-40 bg-gradient-to-r from-transparent via-[#D4A12A] to-transparent" style={{ animationDelay: "800ms" }} />
          <p className="rise text-white/60 mt-5 text-sm" style={{ animationDelay: "1100ms" }}>Showhome Interactive</p>
        </div>
      )}
    </div>
  );
}

export function GlideCaption({ cap }) {
  const text = cap?.text || "";
  const facts = cap?.facts || [];
  return (
    <div className={`absolute inset-x-0 bottom-0 pt-40 pb-28 z-20 text-center pointer-events-none transition-opacity duration-1000 bg-gradient-to-t from-black/70 via-black/35 to-transparent ${cap ? "opacity-100" : "opacity-0"}`} data-testid="glide-caption">
      <div key={text + facts.length} className="px-6">
        {facts.some((f) => f.k === "status") && <FactChips facts={facts.filter((f) => f.k === "status")} />}
        <p className="font-serif text-3xl sm:text-5xl text-white drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)] rise">{text}</p>
        <FactChips facts={facts.filter((f) => f.k !== "status")} />
      </div>
      {cap && <p className="text-xs text-white/70 mt-3">Tap anywhere to explore yourself</p>}
    </div>
  );
}

export const webHref = (u) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);

export function AgentActions({ agent, compact }) {
  const btn = `inline-flex items-center gap-1.5 rounded-full font-semibold transition-colors ${compact ? "h-9 px-3 text-xs" : "h-10 px-4 text-sm"}`;
  const stop = (e) => e.stopPropagation();
  return (
    <div className="flex flex-wrap justify-center gap-2" data-testid="agent-actions">
      {agent.phone && <a href={`tel:${agent.phone.replace(/[^\d+]/g, "")}`} onPointerDown={stop} className={`${btn} cine-btn`} data-testid="agent-call"><Phone className="w-4 h-4" />{agent.phone}</a>}
      {agent.email && <a href={`mailto:${agent.email}`} onPointerDown={stop} className={`${btn} cine-ghost`} data-testid="agent-email"><Mail className="w-4 h-4" />Email</a>}
      {agent.website && <a href={webHref(agent.website)} target="_blank" rel="noreferrer" onPointerDown={stop} className={`${btn} cine-ghost`} data-testid="agent-website"><Globe className="w-4 h-4" />Website</a>}
    </div>
  );
}

export function AgentMini({ agent, size = "md" }) {
  const ph = size === "lg" ? "w-16 h-16" : "w-12 h-12";
  return (
    <div className="flex items-center gap-3 text-left" data-testid="agent-mini">
      {agent.photo_url ? <img src={asset(agent.photo_url)} alt={agent.name} className={`${ph} rounded-full object-cover ring-2 ring-[#D4A12A]/80 shrink-0`} /> : <span className={`${ph} rounded-full grid place-items-center bg-white/15 ring-2 ring-[#D4A12A]/80 shrink-0`}><UserRound className="w-6 h-6" /></span>}
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.22em] opacity-70">Presented by</p>
        <p className={`font-serif leading-tight truncate ${size === "lg" ? "text-xl" : "text-lg"}`} data-testid="agent-mini-name">{agent.name}</p>
        <p className="text-xs opacity-75 truncate">{[agent.title, agent.brokerage].filter(Boolean).join(" · ")}</p>
      </div>
      {agent.logo_url && <img src={asset(agent.logo_url)} alt={agent.brokerage} className="h-10 max-w-[7rem] object-contain ml-2 shrink-0" />}
    </div>
  );
}

export function AgentBadge({ agent, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`cine-card rounded-3xl shadow-2xl ${open ? "p-4 w-[min(22rem,calc(100vw-1.5rem))]" : "p-1.5 pr-4"} ${className}`} data-testid="agent-badge">
      {open ? (
        <div className="space-y-3">
          <div className="flex items-start gap-2"><div className="flex-1 min-w-0"><AgentMini agent={agent} /></div><button onClick={() => setOpen(false)} className="p-1 opacity-70 hover:opacity-100" data-testid="agent-badge-close"><X className="w-4 h-4" /></button></div>
          {agent.bio && <p className="text-xs opacity-80 line-clamp-4">{agent.bio}</p>}
          <AgentActions agent={agent} compact />
        </div>
      ) : (
        <button onClick={() => setOpen(true)} className="flex items-center gap-2" data-testid="agent-badge-open">
          {agent.photo_url ? <img src={asset(agent.photo_url)} alt={agent.name} className="w-9 h-9 rounded-full object-cover ring-2 ring-[#D4A12A]/80" /> : <span className="w-9 h-9 rounded-full grid place-items-center ring-2 ring-[#D4A12A]/80"><UserRound className="w-4 h-4" /></span>}
          <span className="text-left leading-tight"><span className="block text-[9px] uppercase tracking-[0.2em] opacity-70">Presented by</span><span className="block text-sm font-semibold max-w-[9rem] truncate">{agent.name}</span></span>
        </button>
      )}
    </div>
  );
}

export function ClosingCard({ show, house, builder, unit, onBook, bookLabel, agent }) {
  const facts = homeFacts(CLOSING_FACTS, house, null, unit);
  return (
    <div className={`absolute inset-0 z-30 grid place-items-center px-4 transition-opacity duration-700 ${show ? "opacity-100" : "opacity-0 pointer-events-none"}`} data-testid="glide-closing">
      <div className="rise cine-card w-full max-w-3xl rounded-[2rem] text-center p-8 sm:p-10 shadow-2xl">
        {builder.logo_url && <img src={asset(builder.logo_url)} alt={builder.name} className="h-10 mx-auto mb-5 object-contain" />}
        <FactChips facts={facts.filter((f) => f.k === "status")} />
        <h2 className="font-serif text-3xl sm:text-4xl" data-testid="glide-closing-name">{house.name}</h2>
        {house.base_price > 0 && <p className="cine-price font-serif text-2xl mt-2" data-testid="glide-closing-price">{money(house.base_price)}</p>}
        <FactChips big facts={facts.filter((f) => f.k !== "status")} />
        <button onPointerDown={(e) => e.stopPropagation()} onClick={onBook} className="cine-btn mt-7 h-12 px-7 rounded-full font-semibold inline-flex items-center gap-2 transition-colors" data-testid="glide-closing-book"><CalendarDays className="w-4 h-4" />{bookLabel}</button>
        {agent && <div className="mt-7 pt-6 border-t border-current/10 flex flex-col items-center gap-4" style={{ borderColor: "rgba(127,127,127,0.25)" }} data-testid="glide-closing-agent"><AgentMini agent={agent} size="lg" /><AgentActions agent={agent} /></div>}
      </div>
    </div>
  );
}

export function ExtraButtons({ Btn, builder, hasSuites, audio, muted, onMute, onOpen }) {
  const r = builder.reviews;
  return (
    <>
      {audio && <Btn onClick={onMute} title={muted ? "Play music" : "Mute music"} data-testid="viewer-mute">{muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}</Btn>}
      {(builder.locations_info || []).length > 0 && <Btn onClick={() => onOpen("find")} className="hidden md:inline-flex" data-testid="viewer-find-us"><MapPin className="w-4 h-4" /><span className="hidden 2xl:inline">Find us</span></Btn>}
      {r && (r.rating || r.url || r.quotes?.length > 0) && <Btn onClick={() => onOpen("reviews")} className="hidden md:inline-flex" data-testid="viewer-reviews"><Star className="w-4 h-4 fill-current text-amber-400" />{r.rating ? <span>{r.rating.toFixed(1)}</span> : <span className="hidden 2xl:inline">Reviews</span>}</Btn>}
      {hasSuites && <Btn onClick={() => onOpen("compare")} className="hidden lg:inline-flex" data-testid="viewer-compare"><Columns3 className="w-4 h-4" /><span className="hidden 2xl:inline">Compare</span></Btn>}
      <Btn onClick={() => onOpen("share")} data-testid="viewer-share-tour"><Share2 className="w-4 h-4" /></Btn>
    </>
  );
}

const Shell = ({ title, onClose, wide, children, testid }) => (
  <Dialog open onOpenChange={onClose}>
    <DialogContent className={`${wide ? "sm:max-w-4xl" : "sm:max-w-lg"} max-h-[88vh] overflow-y-auto`} data-testid={testid}>
      <DialogHeader><DialogTitle className="font-serif text-2xl">{title}</DialogTitle></DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

function FindUs({ builder, onClose }) {
  const locs = builder.locations_info || [];
  const [i, setI] = useState(0);
  const l = locs[i] || {};
  return (
    <Shell title="Find us" onClose={onClose} testid="find-us-dialog">
      {locs.length > 1 && <div className="flex gap-2 flex-wrap">{locs.map((x, k) => <button key={k} onClick={() => setI(k)} className={`h-9 px-4 rounded-full text-sm border ${k === i ? "bg-slate-900 text-white border-slate-900" : ""}`} data-testid={`find-us-tab-${k}`}>{x.name || `Location ${k + 1}`}</button>)}</div>}
      {l.address && <iframe title="map" className="w-full h-56 rounded-2xl border" loading="lazy" src={`https://www.google.com/maps?q=${encodeURIComponent(l.address)}&output=embed`} />}
      <div className="space-y-2 text-sm">
        {l.address && <p className="flex gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />{l.address}</p>}
        {l.hours && <p className="flex gap-2 whitespace-pre-line"><Clock className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />{l.hours}</p>}
        {l.parking && <p className="flex gap-2 whitespace-pre-line"><Car className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />{l.parking}</p>}
        {l.phone && <a href={`tel:${l.phone}`} className="flex gap-2 underline underline-offset-4"><Phone className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />{l.phone}</a>}
      </div>
      {l.address && <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(l.address)}`} target="_blank" rel="noopener noreferrer" className="h-11 rounded-full bg-slate-900 text-white text-sm font-semibold inline-flex items-center justify-center gap-2" data-testid="find-us-directions">Get directions<ExternalLink className="w-4 h-4" /></a>}
    </Shell>
  );
}

function Reviews({ builder, onClose }) {
  const r = builder.reviews || {};
  return (
    <Shell title="What guests say" onClose={onClose} testid="reviews-dialog">
      {r.rating && <div className="flex items-center gap-3"><span className="font-serif text-5xl">{r.rating.toFixed(1)}</span><div><div className="flex">{[1, 2, 3, 4, 5].map((s) => <Star key={s} className={`w-5 h-5 ${s <= Math.round(r.rating) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}</div>{r.count ? <p className="text-xs text-slate-500">{r.count} Google reviews</p> : null}</div></div>}
      <div className="space-y-3">{(r.quotes || []).filter((q) => q.text).map((q, k) => <blockquote key={k} className="rounded-2xl bg-slate-50 border p-4 text-sm leading-relaxed">“{q.text}”{q.author && <footer className="text-xs text-slate-500 mt-2">{q.author}</footer>}</blockquote>)}</div>
      {r.url && <a href={r.url} target="_blank" rel="noopener noreferrer" className="h-11 rounded-full bg-slate-900 text-white text-sm font-semibold inline-flex items-center justify-center gap-2" data-testid="reviews-leave">Leave a Google review<ExternalLink className="w-4 h-4" /></a>}
    </Shell>
  );
}

function Compare({ hotspots, onSelect, onBook, onClose }) {
  const suites = hotspots.filter((h) => h.kind === "room");
  return (
    <Shell title={`Compare ${vt().rooms}`} onClose={onClose} wide testid="compare-dialog">
      <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(200px,1fr))]">
        {suites.map((s) => {
          const inside = hotspots.filter((x) => x.parent_id === s.id);
          return (
            <div key={s.id} className="rounded-2xl border overflow-hidden flex flex-col" data-testid={`compare-${s.id}`}>
              {s.photos?.[0] ? <img src={asset(s.photos[0])} alt="" className="aspect-[4/3] object-cover" /> : <div className="aspect-[4/3] bg-slate-100" />}
              <div className="p-4 flex-1 flex flex-col gap-2">
                <p className="font-semibold">{s.title}</p>
                {s.dimensions && <p className="text-xs text-slate-500">{s.dimensions}</p>}
                <div className="flex flex-wrap gap-1">{inside.map((m) => <span key={m.id} className="text-[11px] rounded-full bg-slate-100 px-2 py-0.5">{m.title}</span>)}</div>
                <div className="mt-auto pt-2 grid grid-cols-2 gap-2">
                  <button onClick={() => { onClose(); onSelect(s); }} className="h-9 rounded-full border text-xs font-semibold" data-testid={`compare-view-${s.id}`}>View</button>
                  <button onClick={() => onBook(s)} className="h-9 rounded-full bg-slate-900 text-white text-xs font-semibold" data-testid={`compare-book-${s.id}`}>Book</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Shell>
  );
}

function ShareTour({ house, onClose }) {
  const url = `${window.location.origin}/view/${house.id}`;
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { window.prompt("Copy this link", url); } };
  const native = async () => { try { await navigator.share({ title: house.name, text: `Come with me? Take a look at ${house.name}`, url }); } catch { /* cancelled */ } };
  return (
    <Shell title="Share this tour" onClose={onClose} testid="share-dialog">
      <div className="grid place-items-center"><QRCodeSVG value={url} size={180} data-testid="share-qr" /></div>
      <p className="text-xs text-slate-500 text-center">Scan to open on a phone</p>
      <div className="flex gap-2"><input readOnly value={url} className="flex-1 h-11 rounded-full border px-4 text-sm bg-slate-50" data-testid="share-url" /><button onClick={copy} className="h-11 px-4 rounded-full bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2" data-testid="share-copy"><Copy className="w-4 h-4" />Copy</button></div>
      {navigator.share && <button onClick={native} className="h-11 rounded-full border text-sm font-semibold" data-testid="share-native">Send to a friend</button>}
    </Shell>
  );
}

export function ExtraDialog({ which, close, builder, house, hotspots, onSelect, onBook }) {
  if (which === "find") return <FindUs builder={builder} onClose={close} />;
  if (which === "reviews") return <Reviews builder={builder} onClose={close} />;
  if (which === "compare") return <Compare hotspots={hotspots} onSelect={onSelect} onBook={onBook} onClose={close} />;
  if (which === "share") return <ShareTour house={house} onClose={close} />;
  return null;
}
